import React, { useEffect, useRef } from 'react';
import { PiBuildingOfficeLight, PiUserCircleCheckLight, PiFadersLight } from 'react-icons/pi';
import { useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch } from '../store';
import { checkOrganizationDomain } from '../store/actions/OnboardingActions';
import apis from '../utils/apis';
import { getAuth0Email } from '../auth0-integration/utils/auth0EmailUtils';
import UserOnboardingStep from '../components/Onboarding/UserOnboardingStep';
import PreferencesSetStep from '../components/Onboarding/PreferencesSetStep';
import LoadingScreen from '../auth0-integration/components/LoadingScreen';
import OrganizationCheckStep from '../components/Onboarding/OrganizationCheckStep';
import WelcomeStep from '../components/Onboarding/WelcomeStep';
import { ONBOARDING_MAX_WIDTH } from '../components/Onboarding/OnboardingStepCard';
import WizardStepRail, {
  WizardStepRailItem,
} from '../components/shared/FormComponents/WizardStepRail';
import Spinner from '../components/shared/TailwindComponents/Spinner';
import Alert from '../components/shared/TailwindComponents/Alert';

/**
 * The three rail steps. `welcome` is deliberately absent — it is the door into
 * the flow, not a step of it, which is why the rail is hidden there.
 *
 * Labels are untranslated: `WizardStepRail` runs them through `t()`.
 */
type OnboardingRailStep = 'organization' | 'user' | 'preferences';

const ONBOARDING_STEPS: WizardStepRailItem<OnboardingRailStep>[] = [
  { key: 'organization', label: 'Organization', icon: <PiBuildingOfficeLight className="w-4 h-4" /> },
  { key: 'user', label: 'Profile', icon: <PiUserCircleCheckLight className="w-4 h-4" /> },
  { key: 'preferences', label: 'Preferences', icon: <PiFadersLight className="w-4 h-4" /> },
];

const OnboardingPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isAuthenticated, user: auth0User } = useAuth0();
  const onboarding = useSelector((state: RootState) => state.onboarding);
  const isUserLoaded = useSelector((state: RootState) => !!state.user?.userInfo?.UserID);
  const orgCheckTriggeredRef = useRef(false);
  const profileFetchTriggeredRef = useRef(false);

  const realEmail = getAuth0Email(auth0User);

  // Fetch user's profile on mount to check organization_id status
  // IMPORTANT: Only call after useAuth0Profile has completed /auth/login (check userInfo exists)
  useEffect(() => {
    if (!profileFetchTriggeredRef.current && isAuthenticated && isUserLoaded && !onboarding.user) {
      profileFetchTriggeredRef.current = true;

      const fetchUserProfile = async () => {
        try {
          const response = await apis.get('/onboarding/get-user-info');
          if (response.data.success && response.data.user) {
            dispatch({
              type: 'ONBOARDING_GET_USER_INFO_SUCCESS',
              payload: response.data,
            });
          }
        } catch (error) {
          // Silently handle error
        }
      };

      fetchUserProfile();
    }
  }, [isAuthenticated, isUserLoaded, dispatch, onboarding.user]);

  // Trigger organization domain check ONLY when on organization step with no org selected
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      !orgCheckTriggeredRef.current &&
      isAuthenticated &&
      realEmail &&
      onboarding.currentStep === 'organization' &&
      !onboarding.selectedOrganization &&
      onboarding.user !== null &&
      !onboarding.user?.organization_id &&
      !onboarding.isLoading &&
      onboarding.matchingOrganizations.length === 0
    ) {
      orgCheckTriggeredRef.current = true;
      dispatch(checkOrganizationDomain(realEmail) as any);
    }
  }, [
    isAuthenticated,
    realEmail,
    dispatch,
    onboarding.currentStep,
    onboarding.selectedOrganization,
    onboarding.user?.organization_id,
    onboarding.user,
    onboarding.isLoading,
    onboarding.matchingOrganizations.length,
  ]);

  // Show loading screen while initial user data is being fetched or if step is not set
  if (!isAuthenticated) {
    return <LoadingScreen />;
  }

  if (!onboarding.user || onboarding.currentStep === null) {
    return <LoadingScreen />;
  }

  const isCenterStep = onboarding.currentStep === 'welcome' || onboarding.currentStep === 'organization';

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      {/* Step rail — the shared wizard rail, hidden on the welcome door.
          Progress is read from step ORDER, which is what the old bespoke rail's
          three tangles of `organization_id && first_name` conditions were
          reconstructing: the flow only reaches step N once N-1 is done. */}
      {onboarding.currentStep !== 'welcome' && (
        <div className="glass !border-x-0 !border-t-0 relative z-40 flex-shrink-0">
          <WizardStepRail
            steps={ONBOARDING_STEPS}
            currentStep={onboarding.currentStep as OnboardingRailStep}
            className="px-4 py-4"
            maxWidth={ONBOARDING_MAX_WIDTH}
          />
        </div>
      )}

      {/* Content Area - Flows below progress bar */}
      <div className="flex-1 min-h-0 w-full overflow-y-auto custom-scrollbar-main">
        {onboarding.isLoading && (
          <div className="flex min-h-[60vh] w-full items-center justify-center px-4">
            <Spinner
              size="lg"
              centered
              label={
                onboarding.currentStep === 'user'
                  ? t('Loading profile and role...')
                  : t('Setting up your account...')
              }
            />
          </div>
        )}

        {!onboarding.isLoading && isCenterStep && (
          <div className="flex min-h-[80vh] w-full items-center justify-center px-4 py-8">
            {onboarding.currentStep === 'welcome' && <WelcomeStep />}
            {onboarding.currentStep === 'organization' && <OrganizationCheckStep />}
          </div>
        )}

        {!onboarding.isLoading && !isCenterStep && (
          <div className="w-full px-4 py-8">
            <div className={`mx-auto w-full space-y-6 ${ONBOARDING_MAX_WIDTH}`}>
              {/* User Details Step - Only available if org_id exists */}
              {onboarding.currentStep === 'user' && onboarding.user?.organization_id && <UserOnboardingStep />}

              {/* Preferences Step - Only available if org_id exists and user details are filled */}
              {onboarding.currentStep === 'preferences' &&
                onboarding.user?.organization_id &&
                onboarding.user?.first_name && <PreferencesSetStep />}

              {onboarding.error && <Alert type="error" message={onboarding.error} />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingPage;
